import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  Spinner,
  useColorModeValue,
  useDisclosure
} from "@chakra-ui/react";
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoOutlineIcon,
  StarIcon,
  ViewIcon
} from "@chakra-ui/icons";
import { getContractHistory } from "../../../Services/api";
import { HistoryItemWithServices } from "./HistoryItemWithServices";
import { MainPackageCard } from "./PackageCards";
import { ContractStatCard } from "./ContractStatCard";
import { StatBadge } from "./StatBadge";

export const ContractWithServices = ({ contract, texts }) => {
  const { isOpen, onToggle } = useDisclosure();
  const [histories, setHistories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("purple.200", "purple.800");
  const headerBorderColor = useColorModeValue("purple.200", "purple.800");
  const bodyBg = useColorModeValue("white", "gray.800");
  const mutedBg = useColorModeValue("gray.50", "gray.700");
  const mutedBorderColor = useColorModeValue("gray.200", "gray.600");
  const titleColor = useColorModeValue("gray.800", "white");
  const headingColor = useColorModeValue("gray.800", "white");

  const mainPackage = contract.Package;
  const hasMainPackage = !!mainPackage;

  useEffect(() => {
    if (!isOpen || histories.length > 0) return;

    let isActive = true;
    setIsLoading(true);
    setError(null);

    getContractHistory(contract.ContractId)
      .then((response) => {
        if (!isActive) return;
        setHistories(response.data?.data || []);
      })
      .catch((err) => {
        if (!isActive) return;
        setError(err.message || texts?.fetchError);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [contract.ContractId, histories.length, isOpen, texts?.fetchError]);

  const stats = {
    mainPackage: hasMainPackage ? 1 : 0,
    histories: histories.length,
    completed: mainPackage?.IsApproved ? 1 : 0,
    pending: mainPackage && !mainPackage.IsApproved ? 1 : 0
  };

  return (
    <Card
      width="100%"
      border="2px"
      borderColor={borderColor}
      shadow="xl"
      bg={cardBg}
      _hover={{ shadow: "2xl" }}
      transition="all 0.3s"
      borderRadius="2xl"
      overflow="hidden"
    >
      <CardHeader
        bgGradient="linear(to-r, purple.500, purple.600)"
        borderBottom="2px"
        borderColor={headerBorderColor}
        py={1}
        px={2}
        color="white"
      >
        <Flex justify="space-between" align="center" cursor="pointer" onClick={onToggle}>
          <VStack align="start" spacing={3} flex={1}>
            <HStack spacing={3}>
              <Box
                w="60px"
                h="60px"
                bg="white"
                borderRadius="xl"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                  📄
                </Text>
              </Box>
              <Box>
                <Heading size="xl" color={titleColor}>
                  {contract.ContractTaskName || texts?.unnamedContract}
                </Heading>
                <HStack spacing={4} mt={2}>
                  <HStack spacing={2}>
                    <Icon as={ViewIcon} color="purple.200" />
                    <Text color="purple.100">
                      {texts?.contractId}: {contract.ContractId}
                    </Text>
                  </HStack>
                  {hasMainPackage && (
                    <Badge
                      colorScheme="whiteAlpha"
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="full"
                      variant="solid"
                    >
                      🎯 {texts?.hasMainPackage}
                    </Badge>
                  )}
                </HStack>
              </Box>
            </HStack>
          </VStack>

          <Flex align="center" gap={2}>
            <HStack spacing={3}>
              <StatBadge label={texts?.histories} value={histories.length} color="blue" size="md" />
              <StatBadge label={texts?.completed} value={stats.completed} color="green" size="md" />
            </HStack>
            <Icon
              as={isOpen ? ChevronUpIcon : ChevronDownIcon}
              w={8}
              h={8}
              color="white"
              transition="transform 0.3s"
              transform={isOpen ? "rotate(180deg)" : "rotate(0)"}
            />
          </Flex>
        </Flex>
      </CardHeader>

      <Collapse in={isOpen} animateOpacity>
        <CardBody py={2} bg={bodyBg}>
          {isLoading ? (
            <VStack spacing={4} py={12}>
              <Spinner size="lg" color="purple.500" thickness="4px" />
              <Text color="gray.600">{texts?.loadingHistories}</Text>
            </VStack>
          ) : error ? (
            <Alert status="error" borderRadius="lg" mb={4}>
              {error}
            </Alert>
          ) : (
            <Tabs
              variant="enclosed-colored"
              colorScheme="purple"
              isFitted
              onChange={setActiveTab}
              index={activeTab}
            >
              <TabList mb={0.5}>
                <Tab fontWeight="semibold">
                  <HStack spacing={2}>
                    <Icon as={StarIcon} />
                    <Text>{texts?.mainPackage}</Text>
                  </HStack>
                </Tab>
                <Tab fontWeight="semibold" isDisabled={histories.length === 0}>
                  <HStack spacing={2}>
                    <Icon as={CalendarIcon} />
                    <Text>
                      {texts?.histories} ({histories.length})
                    </Text>
                  </HStack>
                </Tab>
                <Tab fontWeight="semibold">
                  <HStack spacing={2}>
                    <Icon as={InfoOutlineIcon} />
                    <Text>{texts?.statistics}</Text>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <MainPackageCard mainPackage={mainPackage} texts={texts} />
                </TabPanel>

                <TabPanel px={0}>
                  {histories.length > 0 ? (
                    <VStack spacing={4} align="stretch">
                      {histories.map((history, index) => (
                        <HistoryItemWithServices
                          key={history.HistoryId || index}
                          historyItem={history}
                          texts={texts}
                        />
                      ))}
                    </VStack>
                  ) : (
                    <Box
                      textAlign="center"
                      py={12}
                      bg={mutedBg}
                      borderRadius="xl"
                    >
                      <Icon as={InfoOutlineIcon} color="gray.400" boxSize={12} mb={4} />
                      <Text fontSize="lg" color="gray.500">
                        {texts?.noServicesData}
                      </Text>
                    </Box>
                  )}
                </TabPanel>

                <TabPanel px={0}>
                  <Box
                    p={2}
                    bg={mutedBg}
                    borderRadius="2xl"
                    border="1px"
                    borderColor={mutedBorderColor}
                  >
                    <Heading size="lg" color={headingColor} mb={6}>
                      📊 {texts?.statistics}
                    </Heading>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                      <ContractStatCard label={texts?.mainPackage} value={stats.mainPackage} color="purple" icon="🎯" />
                      <ContractStatCard label={texts?.histories} value={stats.histories} color="blue" icon="📋" />
                      <ContractStatCard label={texts?.completed} value={stats.completed} color="green" icon="✅" />
                      <ContractStatCard label={texts?.pending} value={stats.pending} color="orange" icon="⏳" />
                    </SimpleGrid>

                    {hasMainPackage && (
                      <Box mt={8}>
                        <Text fontWeight="semibold" color="gray.600" mb={4}>
                          {texts?.status}
                        </Text>
                        <Progress
                          value={mainPackage?.IsApproved ? 100 : 50}
                          colorScheme={mainPackage?.IsApproved ? "green" : "orange"}
                          size="lg"
                          borderRadius="full"
                          height="25px"
                          hasStripe
                          isAnimated={!mainPackage?.IsApproved}
                        />
                      </Box>
                    )}
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
};
